/* eslint-disable react/no-unescaped-entities */
import { Link } from "react-router-dom";
import { ShieldCheck, Lock, KeyRound, Fingerprint, FileCheck, Eye, ServerCog, ScrollText } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo, { ldBreadcrumb } from "@/components/Seo";

const PILLARS = [
  {
    icon: Lock,
    title: "Chiffrement de bout en bout",
    points: [
      "TLS 1.3 sur toutes les communications (HTTPS strict).",
      "Mots de passe hashés via bcrypt (10 rounds).",
      "Tokens JWT stockés en cookies httpOnly — inaccessibles au JavaScript navigateur.",
    ],
  },
  {
    icon: Fingerprint,
    title: "Authentification renforcée",
    points: [
      "Politique stricte : minimum 10 caractères, majuscule, chiffre, blacklist anti-mots-courants.",
      "Double authentification 2FA (TOTP) via Google Authenticator, Authy, 1Password.",
      "10 codes de récupération chiffrés pour les imprévus.",
      "Récupération de mot de passe par token cryptographique 256-bit, expiration 60 min, usage unique.",
    ],
  },
  {
    icon: KeyRound,
    title: "Contrôle d'accès granulaire",
    points: [
      "5 rôles distincts (visiteur, client, spécialiste, spécialiste vérifié, administrateur).",
      "24 permissions granulaires (RBAC).",
      "Isolation stricte des données entre utilisateurs (zero leak cross-tenant).",
      "Sessions sécurisées avec révocation immédiate possible.",
    ],
  },
  {
    icon: ScrollText,
    title: "Conformité RGPD (Art. 32)",
    points: [
      "Hébergement Union Européenne (OVHcloud France).",
      "Journal d'audit complet : toutes les actions admin tracées, conservation 2 ans.",
      "Droit à l'oubli : suppression sur demande sous 30 jours.",
      "Consentements horodatés (Vapi voice, cookies, communications).",
      "Délégué à la protection des données joignable à dpo@confilex.com.",
    ],
  },
  {
    icon: Eye,
    title: "Confidentialité des dossiers",
    points: [
      "Documents stockés chiffrés au repos (AES-256).",
      "Déduplication par empreinte SHA-256 — aucun document n'est jamais transmis à un tiers.",
      "Classification IA en local — les contenus ne quittent jamais l'infrastructure CONFILEX.",
      "Accès spécialiste limité aux dossiers explicitement payés et débloqués.",
    ],
  },
  {
    icon: ServerCog,
    title: "Infrastructure & monitoring",
    points: [
      "Validation HMAC des webhooks entrants (Twilio, Stripe, LinkedIn).",
      "Validation cryptographique du protocole OAuth 2.0 + state CSRF.",
      "Monitoring d'erreurs en temps réel avec scrubbing PII automatique.",
      "Sauvegardes chiffrées quotidiennes.",
    ],
  },
];

const CERTIFICATIONS = [
  { label: "Hébergement", value: "OVHcloud Paris (P19)" },
  { label: "Paiements", value: "Stripe PCI-DSS niveau 1" },
  { label: "Voix IA", value: "Vapi + ElevenLabs (Tier 1 chiffrement)" },
  { label: "Email", value: "DKIM, SPF, DMARC alignés" },
];

export default function Security() {
  return (
    <div className="min-h-screen bg-[#F8F6F1] text-[#0B3A2A]">
      <Seo
        title="Sécurité et confidentialité — TLS 1.3, RGPD, hébergement souverain France"
        description="CONFILEX applique les standards de sécurité bancaire : TLS 1.3, mots de passe bcrypt, 2FA, chiffrement au repos, hébergement souverain OVHcloud France, conformité RGPD complète."
        canonical="/security"
        keywords={["sécurité confilex", "RGPD", "TLS 1.3", "hébergement souverain France", "OVH", "chiffrement", "2FA"]}
        jsonLd={[
          ldBreadcrumb([{ name: "Accueil", url: "/" }, { name: "Sécurité", url: "/security" }]),
        ]}
      />
      <Navbar />
      <main className="pt-24 pb-20" data-testid="security-page">
        {/* Hero */}
        <section className="max-w-5xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#0B3A2A] text-[#C9A961] flex items-center justify-center shrink-0">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#C9A961] font-medium">
              Sécurité & Confidentialité
            </p>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight">
            La sécurité des dossiers de nos clients<br />
            <span className="text-[#C9A961]">n'est pas une option.</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-700 mt-6 max-w-3xl leading-relaxed">
            CONFILEX traite chaque jour des informations sensibles relevant du secret professionnel : actes notariés, déclarations fiscales, dossiers contentieux, pièces patrimoniales.
            Nous appliquons les standards de sécurité des banques privées, audités et documentés.
          </p>
        </section>

        {/* 6 piliers */}
        <section className="max-w-5xl mx-auto px-6 mt-16">
          <div className="grid sm:grid-cols-2 gap-5">
            {PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="bg-white rounded-2xl p-6 border border-slate-200/60 hover:shadow-lg transition-shadow" data-testid={`security-pillar-${p.title}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-[#0B3A2A]/5 text-[#0B3A2A] flex items-center justify-center">
                      <Icon className="w-5 h-5" />
                    </div>
                    <h2 className="font-serif text-xl">{p.title}</h2>
                  </div>
                  <ul className="space-y-2">
                    {p.points.map((pt) => (
                      <li key={pt} className="text-sm text-slate-700 leading-relaxed flex gap-2">
                        <span className="text-[#C9A961] shrink-0">•</span>
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>

        {/* Certifications strip */}
        <section className="max-w-5xl mx-auto px-6 mt-16">
          <div className="bg-[#0B3A2A] rounded-2xl p-8 text-white">
            <div className="flex items-center gap-3 mb-6">
              <FileCheck className="w-5 h-5 text-[#C9A961]" />
              <h2 className="font-serif text-xl text-[#C9A961]">Partenaires & certifications</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {CERTIFICATIONS.map((c) => (
                <div key={c.label} className="border-l-2 border-[#C9A961]/30 pl-4">
                  <p className="text-xs uppercase tracking-wider text-[#C9A961]/70">{c.label}</p>
                  <p className="text-sm text-white/90 mt-1">{c.value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Best practices côté user */}
        <section className="max-w-5xl mx-auto px-6 mt-16">
          <h2 className="font-serif text-3xl mb-4">Nos recommandations pour votre compte</h2>
          <p className="text-slate-700 mb-6">
            La sécurité est un partenariat : nous chiffrons et protégeons, vous adoptez quelques gestes simples.
          </p>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl p-5 border border-slate-200/60">
              <div className="text-xs uppercase tracking-wider text-[#C9A961] mb-2">Action 1</div>
              <h3 className="font-serif text-lg mb-2">Activez la 2FA</h3>
              <p className="text-sm text-slate-600 mb-3">
                Une application d'authentification (Google Authenticator, Authy) protège votre compte même si votre mot de passe est compromis.
              </p>
              <Link to="/profile" className="text-sm text-[#0B3A2A] hover:text-[#C9A961] font-medium" data-testid="security-cta-2fa">
                Activer la 2FA →
              </Link>
            </div>
            <div className="bg-white rounded-xl p-5 border border-slate-200/60">
              <div className="text-xs uppercase tracking-wider text-[#C9A961] mb-2">Action 2</div>
              <h3 className="font-serif text-lg mb-2">Renouvelez votre mot de passe</h3>
              <p className="text-sm text-slate-600 mb-3">
                Recommandé tous les 6 mois. Privilégiez une phrase de passe longue plutôt que des caractères spéciaux compliqués.
              </p>
              <Link to="/forgot-password" className="text-sm text-[#0B3A2A] hover:text-[#C9A961] font-medium" data-testid="security-cta-password">
                Renouveler →
              </Link>
            </div>
            <div className="bg-white rounded-xl p-5 border border-slate-200/60">
              <div className="text-xs uppercase tracking-wider text-[#C9A961] mb-2">Action 3</div>
              <h3 className="font-serif text-lg mb-2">Signaler un incident</h3>
              <p className="text-sm text-slate-600 mb-3">
                Activité suspecte, accès non reconnu, document partagé par erreur ? Contactez-nous immédiatement.
              </p>
              <a href="mailto:security@confilex.com" className="text-sm text-[#0B3A2A] hover:text-[#C9A961] font-medium" data-testid="security-cta-contact">
                security@confilex.com →
              </a>
            </div>
          </div>
        </section>

        {/* Footer pledge */}
        <section className="max-w-5xl mx-auto px-6 mt-16">
          <div className="border-t border-slate-300/60 pt-8 text-center">
            <p className="font-serif text-2xl italic text-[#0B3A2A] max-w-2xl mx-auto leading-relaxed">
              "Les dossiers confiés à CONFILEX bénéficient d'un environnement technique
              exigeant : confidentialité contractuelle, traçabilité des accès et
              cloisonnement strict. Le secret professionnel demeure attaché au
              professionnel réglementé sollicité, dans les conditions propres à sa
              profession."
            </p>
            <p className="text-sm text-slate-600 mt-4">— Charte de confidentialité CONFILEX</p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
